'use client';

import React, { useEffect, useRef, useState } from 'react';
import { SpriteIcon } from '@/components/ui/SvgSprite';

import { toPersianDigits } from '@/utils/persianUtils';

interface ChangeValueProps {
    change: number;
    changeToman?: number;
    className?: string;
    isPercentage?: boolean;
}

const ChangeValue: React.FC<ChangeValueProps> = ({
    change,
    changeToman,
    className,
    isPercentage = true,
}) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const prevChangeRef = useRef<number>(change);

    useEffect(() => {
        if (prevChangeRef.current !== undefined && prevChangeRef.current !== change) {
            setIsAnimating(true);
            const timer = setTimeout(() => {
                setIsAnimating(false);
            }, 1000);
            return () => clearTimeout(timer);
        }
        prevChangeRef.current = change;
    }, [change]);

    const getColor = (value: number): string => {
        if (value > 0) {
            return '#0CAB00';
        } else if (value < 0) {
            return '#EB0000';
        } else {
            return '#808080';
        }
    };

    const effectiveChange =
        changeToman !== undefined && changeToman !== null && changeToman !== 0
            ? changeToman
            : change;
    const color = getColor(effectiveChange);

    const hasToman = changeToman !== undefined && changeToman !== null && changeToman !== 0;

    const formattedToman = hasToman
        ? toPersianDigits(Math.abs(changeToman).toLocaleString('en-US'))
        : '';
    const formattedPercent = !isNaN(change) ? toPersianDigits(Math.abs(change).toFixed(2)) : '۰';

    return (
        <div
            dir='ltr'
            className={`font-yekanBakh inline-flex items-center gap-1 transition-all duration-300 ${
                isAnimating ? 'animate-bounce scale-110' : ''
            } ${className || ''}`}
            style={{ color }}
        >
            {hasToman ? (
                <>
                    <span className='whitespace-nowrap text-[10px] sm:text-xs font-normal'>
                        {formattedToman} ({formattedPercent}%)
                    </span>
                    {effectiveChange !== 0 && (
                        <SpriteIcon
                            name='caret-up'
                            size={10}
                            className={`shrink-0 ${effectiveChange > 0 ? '' : 'rotate-180'}`}
                        />
                    )}
                </>
            ) : (
                <>
                    {effectiveChange !== 0 && (
                        <SpriteIcon
                            name='caret-up'
                            size={10}
                            className={`mb-1 shrink-0 ${effectiveChange > 0 ? '' : 'rotate-180'}`}
                        />
                    )}
                    {!isNaN(change)
                        ? isPercentage
                            ? `${Math.abs(change).toFixed(2)}%`
                            : `${Math.abs(change).toLocaleString('fa-IR')}`
                        : '0'}
                </>
            )}
        </div>
    );
};

export default ChangeValue;
